import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { LoadingSpinner } from '@medtronic/pecc-react-component-library-js';
import { Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { DatasetFile } from '~/api/types';
import { useGetDatasetFilesQuery } from '~/api/v2/hooks';
import { Table, Tooltip } from '~/components';
import { UploadingFile } from '~/hooks/dataset/useDatasetFileUploader';
import { DATE_FORMAT, formatBytes } from '~/utils';
import { DatasetFilesActionCell, LinearProgressWithLabel } from '.';
import { ProgressStatus } from '../types';

interface DatasetFilesPanelProps {
  uploadingFiles: Map<string, UploadingFile>;
  supportiveDocs?: boolean;
}

const DatasetFilesPanel: FC<DatasetFilesPanelProps> = ({ uploadingFiles, supportiveDocs = false }) => {
  const datasetId = +useParams().id!;
  const { data, isLoading } = useGetDatasetFilesQuery({
    datasetId,
    supportiveDocs,
  });

  const shouldRenderLoading = (id: string) => uploadingFiles.get(id)?.status === ProgressStatus.RUNNING;

  const columns: GridColDef<DatasetFile & { id: string }>[] = [
    {
      field: 'name',
      headerName: 'File name',
      sortable: false,
      flex: 2,
      renderCell: ({ row }) => (
        <Tooltip title={<Typography>{row.name}</Typography>}>
          <span>{row.name}</span>
        </Tooltip>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      sortable: false,
      renderCell: ({ row }) => (shouldRenderLoading(row.id) ? <LoadingSpinner spinnerSize="xs" /> : row.type),
    },
    {
      field: 'size',
      renderCell: ({ row }) =>
        shouldRenderLoading(row.id) ? <LoadingSpinner spinnerSize="xs" /> : formatBytes(row?.size),
      headerName: 'Size',
      sortable: false,
    },
    {
      field: 'date_uploaded',
      headerName: 'Date Uploaded',
      sortable: false,
      flex: 1.5,
      renderCell: ({ row }) => format(new Date(row.date_uploaded), DATE_FORMAT),
    },
    {
      field: 'date_obtained',
      headerName: 'Date Obtained',
      sortable: false,
      flex: 1.5,
      renderCell: ({ row }) => format(new Date(row.date_obtained), DATE_FORMAT),
    },
    {
      field: 'progress',
      flex: 2,
      headerName: '',
      sortable: false,
      renderCell: ({ row }) => {
        const uploadingFile = uploadingFiles.get(row.id);

        return (
          <LinearProgressWithLabel
            value={uploadingFile?.status === ProgressStatus.RUNNING ? uploadingFile?.progress : 100}
            status={uploadingFile?.status || ProgressStatus.COMPLETED}
          />
        );
      },
    },
    {
      field: 'actions',
      flex: 1,
      headerName: 'Actions',
      headerAlign: 'right',
      sortable: false,
      renderCell: DatasetFilesActionCell,
    },
  ];

  return (
    <Stack spacing={3} p={3}>
      <Table
        columnsInitialState={{ columnVisibilityModel: { actions: supportiveDocs } }}
        rows={data?.map((file) => ({ ...file, id: file.path }))}
        columns={columns}
        loading={isLoading}
      />
    </Stack>
  );
};
export default DatasetFilesPanel;
